import { createContext, useContext, useEffect, useState, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { supabase } from '../shared/lib/supabase'
import { logAuditEvent } from '../shared/lib/auditLog'

const AuthContext = createContext({})

function parseUserAgent(ua) {
  if (!ua) return { browser: 'Unknown', os: 'Unknown' }
  let browser = 'Unknown'
  if (ua.includes('Firefox/')) browser = 'Firefox ' + (ua.match(/Firefox\/([\d.]+)/)?.[1] || '')
  else if (ua.includes('Edg/')) browser = 'Edge ' + (ua.match(/Edg\/([\d.]+)/)?.[1] || '')
  else if (ua.includes('Chrome/') && !ua.includes('Edg/')) browser = 'Chrome ' + (ua.match(/Chrome\/([\d.]+)/)?.[1] || '')
  else if (ua.includes('Safari/') && !ua.includes('Chrome/')) browser = 'Safari ' + (ua.match(/Version\/([\d.]+)/)?.[1] || '')

  let os = 'Unknown'
  if (ua.includes('Windows')) os = 'Windows'
  else if (ua.includes('Mac OS X')) os = 'macOS ' + (ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || '')
  else if (ua.includes('Linux')) os = 'Linux'
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS'
  else if (ua.includes('Android')) os = 'Android'

  return { browser: browser.trim(), os: os.trim() }
}

function collectLoginMetadata() {
  const ua = navigator.userAgent || ''
  const { browser, os } = parseUserAgent(ua)
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection
  return {
    userAgent: ua,
    browser,
    os,
    platform: navigator.platform || '',
    language: navigator.language || '',
    timezone: Intl.DateTimeFormat?.().resolvedOptions?.()?.timeZone || '',
    screen: { width: screen.width, height: screen.height },
    viewport: { width: window.innerWidth, height: window.innerHeight },
    devicePixelRatio: window.devicePixelRatio || 1,
    cookiesEnabled: navigator.cookieEnabled ?? null,
    online: navigator.onLine ?? null,
    network: conn ? {
      effectiveType: conn.effectiveType || null,
      downlink: conn.downlink || null,
      rtt: conn.rtt || null
    } : null
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [assignedClient, setAssignedClient] = useState(null)
  const [loading, setLoading] = useState(true)
  const userRef = useRef(null)
  const initCompletedRef = useRef(false)

  async function fetchProfile(userId) {
    try {
      // Add timeout to profile fetch to prevent hanging
      const profilePromise = supabase.from('profiles').select('*').eq('id', userId).single()
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Profile fetch timeout')), 8000)
      )

      const { data, error } = await Promise.race([profilePromise, timeoutPromise])

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching profile:', error)
      }
      setProfile(data)

      // Set Sentry user context for error tracking
      if (data) {
        Sentry.setUser({ id: userId, email: data.email, role: data.role })
      }

      // If client role, fetch their assigned client
      if (data?.role === 'client') {
        try {
          const clientPromise = supabase
            .from('client_users')
            .select('client_id, clients(*)')
            .eq('user_id', userId)
            .single()
          const clientTimeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Client assignment fetch timeout')), 5000)
          )

          const clientResult = await Promise.race([clientPromise, clientTimeoutPromise])

          if (clientResult.data?.clients) {
            setAssignedClient(clientResult.data.clients)
          }
        } catch (clientError) {
          console.error('Error fetching client assignment:', clientError.message)
        }
      } else {
        setAssignedClient(null)
      }
    } catch (error) {
      console.error('Error fetching profile:', error.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    let mounted = true

    async function initSession() {
      try {
        // Wrap getSession with a timeout to prevent hanging on corrupted sessions
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Session fetch timeout')), 5000)
        )

        const { data: { session }, error } = await Promise.race([sessionPromise, timeoutPromise])

        if (!mounted) return

        if (error) {
          console.error('Error getting session:', error.message)
          setLoading(false)
          return
        }

        if (session?.user) {
          // Only update if user changed (onAuthStateChange may have already handled this)
          if (userRef.current?.id !== session.user.id) {
            setUser(session.user)
            userRef.current = session.user
            await fetchProfile(session.user.id)
          } else {
            // User already set by onAuthStateChange, just ensure loading is false
            setLoading(false)
          }
        } else {
          setUser(null)
          userRef.current = null
          setProfile(null)
          setLoading(false)
        }
      } catch (error) {
        console.warn('[Auth] Session fetch timed out or failed:', error.message)
        if (!mounted) return
        // If onAuthStateChange already set the user, don't clear it
        // The timeout just means getSession() was slow, not that the session is invalid
        if (userRef.current) {
          // fetchProfile is likely still in progress, just return
          return
        }
        // Only clear state if no user was set by onAuthStateChange
        setLoading(false)
      } finally {
        initCompletedRef.current = true
      }
    }

    initSession()

    // Listen for auth changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return

        // For SIGNED_IN event on page refresh, wait for INITIAL_SESSION instead
        // SIGNED_IN fires before Supabase connection is fully ready, causing queries to hang
        if (event === 'SIGNED_IN' && !userRef.current) {
          setUser(session?.user ?? null)
          userRef.current = session?.user ?? null
          // If init already completed, this is a fresh login — safe to fetch profile now.
          // On page refresh, init hasn't completed yet so we wait for INITIAL_SESSION.
          if (initCompletedRef.current && session?.user) {
            await fetchProfile(session.user.id)
            logAuditEvent({
              category: 'login',
              action: 'user.login',
              description: `User signed in: ${session.user.email}`,
              metadata: collectLoginMetadata()
            })
          }
          return
        }

        // INITIAL_SESSION fires after session is fully validated - safe to make DB calls
        if (event === 'INITIAL_SESSION') {
          const newUser = session?.user ?? null
          if (newUser && (!userRef.current || userRef.current.id === newUser.id)) {
            // User was set by SIGNED_IN, now fetch profile
            setUser(newUser)
            userRef.current = newUser
            await fetchProfile(newUser.id)
          } else if (!newUser) {
            setUser(null)
            userRef.current = null
            setProfile(null)
            setAssignedClient(null)
            setLoading(false)
          }
          return
        }

        const newUser = session?.user ?? null
        // Only update state if user actually changed (prevents unnecessary re-renders on token refresh)
        if (newUser?.id !== userRef.current?.id) {
          setUser(newUser)
          userRef.current = newUser
          if (newUser) {
            await fetchProfile(newUser.id)
          } else {
            setProfile(null)
            setAssignedClient(null)
            setLoading(false)
          }
        }
      }
    )

    // Handle visibility change - refresh session when user returns to tab
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && mounted) {
        try {
          // Attempt to refresh the session when tab becomes visible
          const { data: { session }, error } = await supabase.auth.getSession()
          if (error) {
            console.warn('[Auth] Session refresh on visibility change failed:', error.message)
            return
          }
          // If session exists but user state is stale, update it
          if (session?.user && (!userRef.current || userRef.current.id !== session.user.id)) {
            setUser(session.user)
            userRef.current = session.user
            await fetchProfile(session.user.id)
          } else if (!session && userRef.current) {
            // Session expired while tab was hidden
            console.warn('[Auth] Session expired while tab was inactive')
            setUser(null)
            setProfile(null)
            setAssignedClient(null)
            userRef.current = null
          }
        } catch (err) {
          console.warn('[Auth] Error checking session on visibility change:', err.message)
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Safety fallback: ensure loading is false after max time
    const loadingTimeout = setTimeout(() => {
      if (mounted) {
        setLoading(false)
      }
    }, 8000)

    return () => {
      mounted = false
      clearTimeout(loadingTimeout)
      subscription.unsubscribe()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  async function signUp({ email, password, fullName }) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName
        }
      }
    })
    return { data, error }
  }

  async function signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    return { data, error }
  }

  async function signOut() {
    // Log before clearing session (needs auth for RLS)
    logAuditEvent({
      category: 'login',
      action: 'user.logout',
      description: `User signed out: ${profile?.email || user?.email || 'unknown'}`
    })
    const { error } = await supabase.auth.signOut()
    // Always clear local state, even if there's an error
    setUser(null)
    userRef.current = null
    setProfile(null)
    setAssignedClient(null)
    Sentry.setUser(null)
    return { error }
  }

  async function resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`
    })
    return { data, error }
  }

  async function updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })
    return { data, error }
  }

  // Role helpers
  const isAdmin = ['admin', 'global_admin', 'org_admin', 'org_member'].includes(profile?.role)
  const isClientUser = profile?.role === 'client'

  const value = {
    user,
    profile,
    loading,
    assignedClient,
    isAdmin,
    isClientUser,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    fetchProfile
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
